import { useState } from 'react';
import { auth } from './firebase';
import {
  signInWithPopup,
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  OAuthProvider,
  GithubAuthProvider,
} from 'firebase/auth';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { Link } from 'react-router-dom';
import useAuth from './useAuth';
import './App.css';

const providers = {
  google: () => new GoogleAuthProvider(),
  apple: () => { const p = new OAuthProvider('apple.com'); p.addScope('email'); p.addScope('name'); return p; },
  github: () => new GithubAuthProvider(),
};

export default function AccountMergePage() {
  const { user, loading } = useAuth();
  const [step, setStep] = useState('intro'); // intro | ready | merging | done | error
  const [secondaryInfo, setSecondaryInfo] = useState(null);
  const [mergeToken, setMergeToken] = useState(null);
  const [error, setError] = useState('');
  const [mergeResult, setMergeResult] = useState(null);

  // Email/password sign-in for secondary account
  const [secondaryEmail, setSecondaryEmail] = useState('');
  const [secondaryPassword, setSecondaryPassword] = useState('');
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  if (loading) {
    return (
      <div className="mp-page" style={{ background: '#0f172a', color: '#f8fafc', textAlign: 'center', padding: '120px 24px' }}>
        <p style={{ color: '#64748b' }}>Loading...</p>
      </div>
    );
  }

  if (!user || user.isAnonymous) {
    return (
      <div className="mp-page" style={{ background: '#0f172a', color: '#f8fafc', textAlign: 'center', padding: '120px 24px' }}>
        <h2 style={{ color: '#f8fafc', marginBottom: 16 }}>Sign In Required</h2>
        <p style={{ color: '#94a3b8' }}>
          First, sign in to the account you want to <strong>keep</strong> as your main account.
        </p>
      </div>
    );
  }

  const processSecondaryAuth = async (secondaryUser) => {
    const primaryUid = user.uid;
    const secondaryUid = secondaryUser.uid;
    const secondaryEmailVal = secondaryUser.email || secondaryUser.providerData?.[0]?.email || 'Unknown';

    if (secondaryUid === primaryUid) {
      setError('same-account');
      setIsAuthenticating(false);
      return;
    }

    setSecondaryInfo({
      uid: secondaryUid,
      email: secondaryEmailVal,
      displayName: secondaryUser.displayName || secondaryEmailVal,
    });

    // Generate merge token
    try {
      const functions = getFunctions();
      const generateToken = httpsCallable(functions, 'generateMergeToken');
      const tokenResult = await generateToken({ targetPrimaryUid: primaryUid });
      setMergeToken(tokenResult.data.token);
      setStep('ready');
    } catch (fnErr) {
      setStep('ready');
      setMergeToken(null);
    }
    setIsAuthenticating(false);
  };

  const handleSignIntoSecondaryOAuth = async (providerKey) => {
    setError('');
    setIsAuthenticating(true);
    try {
      const provider = providers[providerKey]();
      const result = await signInWithPopup(auth, provider);
      await processSecondaryAuth(result.user);
    } catch (err) {
      setIsAuthenticating(false);
      if (err.code !== 'auth/popup-closed-by-user') {
        setError(err.message);
      }
    }
  };

  const handleSignIntoSecondaryEmail = async (e) => {
    e.preventDefault();
    if (!secondaryEmail.trim() || !secondaryPassword) return;
    setError('');
    setIsAuthenticating(true);
    try {
      const result = await signInWithEmailAndPassword(auth, secondaryEmail.trim(), secondaryPassword);
      await processSecondaryAuth(result.user);
    } catch (err) {
      setIsAuthenticating(false);
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setError('Incorrect password. Try again or reset your password first.');
      } else if (err.code === 'auth/user-not-found') {
        setError('No account found with that email.');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many attempts. Please wait a moment and try again.');
      } else {
        setError(err.message);
      }
    }
  };

  const handleMerge = async () => {
    if (!secondaryInfo || !mergeToken) {
      setError('Merge token not available. Please try again.');
      return;
    }
    setStep('merging');
    setError('');
    try {
      const functions = getFunctions();
      const merge = httpsCallable(functions, 'mergeAccounts');
      const result = await merge({
        secondaryUid: secondaryInfo.uid,
        verificationToken: mergeToken,
      });
      setMergeResult(result.data);
      setStep('done');
    } catch (err) {
      const msg = err.message || '';
      if (msg.includes('itself') || msg.includes('same')) {
        setError('same-account');
      } else {
        setError(msg.includes('INTERNAL') ? 'Something went wrong on our end. Please try again in a moment.' : `Merge failed: ${msg}`);
      }
      setStep('error');
    }
  };

  const resetFlow = () => {
    setStep('intro');
    setError('');
    setSecondaryInfo(null);
    setMergeToken(null);
    setShowEmailForm(false);
    setSecondaryEmail('');
    setSecondaryPassword('');
    setIsAuthenticating(false);
  };

  return (
    <div className="mp-page" style={{ background: '#0f172a', color: '#f8fafc' }}>
      <section style={{ maxWidth: 600, margin: '0 auto', padding: '60px 24px 48px' }}>
        <Link to="/account" style={{ color: '#3b82f6', fontSize: '0.85rem', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 24 }}>
          &larr; Back to Account
        </Link>

        <h1 style={{ color: '#f8fafc', fontSize: '1.5rem', fontWeight: 700, margin: '0 0 8px' }}>
          Merge Two Accounts
        </h1>
        <p style={{ color: '#94a3b8', fontSize: '0.9rem', lineHeight: 1.6, margin: '0 0 32px' }}>
          If you accidentally created two mChatAI accounts (e.g., one with Apple Sign-In and another
          with email/password), you can combine them here. Compute tokens and data from the old account
          will be transferred to this one.
        </p>

        {/* Step indicator */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 32 }}>
          {['Sign In to Old Account', 'Review & Confirm', 'Merge', 'Done'].map((label, i) => {
            const stepIndex = { intro: 0, ready: 1, merging: 2, done: 3, error: 2 }[step] || 0;
            const active = i <= stepIndex;
            return (
              <div key={label} style={{ flex: 1, textAlign: 'center' }}>
                <div style={{
                  height: 4, borderRadius: 2, marginBottom: 6,
                  background: active ? '#3b82f6' : '#1e293b',
                  transition: 'background 0.3s',
                }} />
                <span style={{ color: active ? '#f8fafc' : '#475569', fontSize: '0.65rem', fontWeight: 600 }}>{label}</span>
              </div>
            );
          })}
        </div>

        {/* Primary account card — always visible */}
        <div style={{
          background: '#1e293b', borderRadius: 12, padding: 16, marginBottom: 16,
          border: '1px solid #10b981',
        }}>
          <div style={{ color: '#10b981', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8, fontWeight: 600 }}>
            Your Current Account (keeping this one)
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 36, height: 36, borderRadius: '50%', background: '#3b82f6',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '0.9rem', color: '#fff', fontWeight: 700,
            }}>
              {(user.displayName || user.email || 'U')[0].toUpperCase()}
            </div>
            <div>
              <div style={{ color: '#f8fafc', fontWeight: 600, fontSize: '0.9rem' }}>
                {user.displayName || 'mChatAI User'}
              </div>
              <div style={{ color: '#64748b', fontSize: '0.78rem' }}>
                {user.email || 'No email'}
              </div>
            </div>
          </div>
        </div>

        {/* Step: Intro — sign into the OLD account */}
        {step === 'intro' && (
          <div style={{
            background: '#0f172a', borderRadius: 12, padding: 24,
            border: '1px solid #334155',
          }}>
            <p style={{ color: '#f8fafc', fontSize: '0.9rem', fontWeight: 600, margin: '0 0 4px' }}>
              Now sign in to your old account
            </p>
            <p style={{ color: '#94a3b8', fontSize: '0.8rem', margin: '0 0 16px' }}>
              This is the account you want to merge into your current one. Its data and tokens will be
              transferred, and it will be deleted afterward.
            </p>

            {/* Same account error — special handling */}
            {error === 'same-account' && (
              <div style={{
                background: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.2)',
                borderRadius: 10, padding: '14px 16px', marginBottom: 16,
              }}>
                <p style={{ color: '#f59e0b', fontSize: '0.85rem', fontWeight: 600, margin: '0 0 6px' }}>
                  That's the same account you're already signed into
                </p>
                <p style={{ color: '#94a3b8', fontSize: '0.78rem', margin: '0 0 8px' }}>
                  You need to sign in with a <strong style={{ color: '#f8fafc' }}>different</strong> account —
                  the old one you want to merge. For example:
                </p>
                <ul style={{ color: '#94a3b8', fontSize: '0.78rem', margin: '0 0 0 16px', padding: 0, lineHeight: 1.8 }}>
                  <li>If you're signed in with <strong style={{ color: '#f8fafc' }}>Apple</strong>, try signing in with <strong style={{ color: '#f8fafc' }}>email/password</strong> or <strong style={{ color: '#f8fafc' }}>Google</strong></li>
                  <li>If you're signed in with <strong style={{ color: '#f8fafc' }}>email</strong>, try signing in with <strong style={{ color: '#f8fafc' }}>Apple</strong> or <strong style={{ color: '#f8fafc' }}>Google</strong></li>
                </ul>
              </div>
            )}

            {/* Generic errors */}
            {error && error !== 'same-account' && (
              <p style={{ color: '#f87171', fontSize: '0.8rem', marginBottom: 12 }}>{error}</p>
            )}

            {/* Email/password sign-in */}
            {showEmailForm ? (
              <form onSubmit={handleSignIntoSecondaryEmail} style={{
                background: '#1e293b', borderRadius: 10, padding: 16, marginBottom: 12,
              }}>
                <p style={{ color: '#94a3b8', fontSize: '0.78rem', margin: '0 0 10px' }}>
                  Sign in with the email and password of the old account:
                </p>
                <input
                  type="email" placeholder="Email of old account" value={secondaryEmail}
                  onChange={(e) => setSecondaryEmail(e.target.value)} required
                  style={inputStyle}
                />
                <input
                  type="password" placeholder="Password" value={secondaryPassword}
                  onChange={(e) => setSecondaryPassword(e.target.value)} required
                  style={{ ...inputStyle, marginTop: 8 }}
                />
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <button type="submit" disabled={isAuthenticating} style={{
                    ...oauthBtnStyle, background: '#3b82f6', border: 'none', flex: 1,
                    opacity: isAuthenticating ? 0.6 : 1,
                  }}>
                    {isAuthenticating ? 'Signing in...' : 'Sign In'}
                  </button>
                  <button type="button" onClick={() => { setShowEmailForm(false); setError(''); }} style={{
                    ...oauthBtnStyle, flex: 0, padding: '10px 16px',
                  }}>
                    Back
                  </button>
                </div>
              </form>
            ) : (
              <>
                <button onClick={() => { setShowEmailForm(true); setError(''); }} style={{
                  ...oauthBtnStyle, width: '100%', marginBottom: 10,
                  background: '#3b82f6', border: 'none',
                }}>
                  Sign in with Email & Password
                </button>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0 10px' }}>
                  <div style={{ flex: 1, height: 1, background: '#334155' }} />
                  <span style={{ color: '#475569', fontSize: '0.7rem', fontWeight: 600 }}>or</span>
                  <div style={{ flex: 1, height: 1, background: '#334155' }} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {Object.entries(providers).map(([key]) => (
                    <button key={key} onClick={() => handleSignIntoSecondaryOAuth(key)}
                      disabled={isAuthenticating} style={{
                        ...oauthBtnStyle, width: '100%', opacity: isAuthenticating ? 0.6 : 1,
                      }}>
                      {key === 'google' ? 'Sign in with Google' : key === 'apple' ? 'Sign in with Apple' : 'Sign in with GitHub'}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Step: Ready — review and confirm */}
        {step === 'ready' && secondaryInfo && (
          <div style={{
            background: '#0f172a', borderRadius: 12, padding: 24,
            border: '1px solid #334155',
          }}>
            <div style={{
              background: '#1e293b', borderRadius: 10, padding: 16, marginBottom: 20,
              border: '1px solid #dc2626',
            }}>
              <div style={{ color: '#dc2626', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8, fontWeight: 600 }}>
                Old Account (will be deleted after merge)
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: '50%', background: '#dc2626',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.9rem', color: '#fff', fontWeight: 700,
                }}>
                  {(secondaryInfo.displayName || 'U')[0].toUpperCase()}
                </div>
                <div>
                  <div style={{ color: '#f8fafc', fontWeight: 600, fontSize: '0.9rem' }}>{secondaryInfo.displayName}</div>
                  <div style={{ color: '#64748b', fontSize: '0.78rem' }}>{secondaryInfo.email}</div>
                </div>
              </div>
            </div>

            <div style={{
              background: 'rgba(245, 158, 11, 0.08)', borderRadius: 8, padding: 12, marginBottom: 20,
              border: '1px solid rgba(245, 158, 11, 0.2)',
            }}>
              <p style={{ color: '#f59e0b', fontSize: '0.8rem', margin: 0, lineHeight: 1.5 }}>
                This will permanently transfer all data and compute tokens from
                <strong> {secondaryInfo.email}</strong> into your current account.
                The old account will be deleted and cannot be recovered.
              </p>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              {mergeToken ? (
                <button onClick={handleMerge} style={{
                  flex: 1, padding: '12px 24px', borderRadius: 8,
                  border: 'none', background: '#dc2626', color: '#fff',
                  fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                }}>
                  Confirm Merge
                </button>
              ) : (
                <div style={{
                  background: '#1e293b', borderRadius: 8, padding: 16, flex: 1,
                  border: '1px solid #334155', textAlign: 'center',
                }}>
                  <p style={{ color: '#94a3b8', fontSize: '0.85rem', margin: 0 }}>
                    Merge is temporarily unavailable. Please try again later.
                  </p>
                </div>
              )}
              <button onClick={resetFlow} style={{
                padding: '12px 20px', borderRadius: 8,
                border: '1px solid #334155', background: '#1e293b', color: '#94a3b8',
                fontSize: '0.85rem', cursor: 'pointer', fontFamily: 'inherit',
              }}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Step: Merging */}
        {step === 'merging' && (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <div style={{ color: '#3b82f6', fontSize: '1.2rem', fontWeight: 600, marginBottom: 8 }}>Merging accounts...</div>
            <p style={{ color: '#64748b', fontSize: '0.85rem' }}>This may take a few seconds.</p>
          </div>
        )}

        {/* Step: Done */}
        {step === 'done' && (
          <div style={{
            background: '#064e3b', borderRadius: 12, padding: 24,
            border: '1px solid #10b981', textAlign: 'center',
          }}>
            <div style={{ fontSize: '2rem', marginBottom: 12 }}>&#10003;</div>
            <h3 style={{ color: '#f8fafc', margin: '0 0 8px' }}>Accounts Merged Successfully</h3>
            <p style={{ color: '#94a3b8', fontSize: '0.85rem', margin: '0 0 16px' }}>
              {mergeResult?.tokensMerged != null
                ? `${mergeResult.tokensMerged.toFixed(1)} compute tokens were transferred to your account.`
                : 'All data has been transferred to your account.'}
            </p>
            <Link to="/account" style={{
              color: '#3b82f6', fontSize: '0.85rem', textDecoration: 'none',
            }}>
              Go to Account
            </Link>
          </div>
        )}

        {/* Step: Error */}
        {step === 'error' && (
          <div style={{
            background: '#1e293b', borderRadius: 12, padding: 24,
            border: '1px solid #dc2626',
          }}>
            {error === 'same-account' ? (
              <>
                <p style={{ color: '#f59e0b', fontSize: '0.9rem', fontWeight: 600, margin: '0 0 8px' }}>
                  You signed into the same account on both sides
                </p>
                <p style={{ color: '#94a3b8', fontSize: '0.85rem', margin: '0 0 16px', lineHeight: 1.5 }}>
                  To merge, you need two <em>different</em> accounts. Make sure the old account
                  uses a different sign-in method (e.g., email/password vs. Apple).
                </p>
              </>
            ) : (
              <p style={{ color: '#f87171', fontSize: '0.85rem', margin: '0 0 16px' }}>{error}</p>
            )}
            <button onClick={resetFlow} style={{
              background: '#334155', color: '#f8fafc', border: 'none', padding: '10px 20px',
              borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.85rem',
            }}>
              Start Over
            </button>
          </div>
        )}
      </section>
    </div>
  );
}

const oauthBtnStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
  padding: '10px 16px', borderRadius: 8, border: '1px solid #334155',
  background: '#1e293b', color: '#f8fafc', fontSize: '0.85rem',
  cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500,
};

const inputStyle = {
  width: '100%', padding: '10px 12px', borderRadius: 8,
  border: '1px solid #334155', background: '#0f172a', color: '#f8fafc',
  fontSize: '0.85rem', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
};
